import {forwardRef, Key, useCallback, useMemo, useState} from "react";
import type {ScrollingContext, TableImplProps} from ".";
import {typedMemo} from "../../../../utils/typescript";
import {ComputeItemKey, ItemContent, TableComponents, TableVirtuoso} from "react-virtuoso";
import {ExtractTableItem, ItemsOrGroups, TableConfig, TableGroup} from "../../../../hooks/table";
import {BaseTableBody, BaseTableHead, BaseTableRoot, BaseTableRow} from "../base";
import {useStickyOffset} from "../../../StickyHeader/useStickyOffset";
import {TableRootProps, TableRowProps} from "@chakra-ui/react";

type GroupHeaderRow<C extends TableConfig> = {type: "header"; group: TableGroup<C>; depth: number; key: Key};
type ItemRow<C extends TableConfig> = {type: "item"; item: ExtractTableItem<C>; key: Key};
type Row<C extends TableConfig> = GroupHeaderRow<C> | ItemRow<C>;

/**
 * What the table's elements read at render time. Virtuoso hands it to every
 * component as a prop, which is what lets the components be built once per
 * table: React compares a component by identity, and a new identity remounts
 * every row it renders.
 */
type VirtuosoContext<C extends TableConfig> = {
  scrolling: ScrollingContext;
  config: C;
  rootProps?: TableRootProps;
  rowProps?: TableRowProps;
  getItemRowProps: (item: ExtractTableItem<C>) => TableRowProps;
  minWidth?: string;
  stickyOffset: number;
};

function generateItemKey<C extends TableConfig>(config: C, item: ExtractTableItem<C>) {
  return `item-${config.extractUniqueKey(item)}`;
}

function generateGroupKey<C extends TableConfig>(group: TableGroup<C>, prefix?: Key) {
  if (prefix == null) return group.key;
  return `${prefix}-${group.key}`;
}

function createComponents<C extends TableConfig>(): TableComponents<Row<C>, VirtuosoContext<C>> {
  return {
    Table: ({context, ...props}) => (
      <BaseTableRoot
        config={context.config}
        {...context.rootProps}
        {...props}
        {...(context.minWidth ? {minWidth: context.minWidth} : {})}
      />
    ),
    // Virtuoso pins the header to the very top of the scrollport. Any sticky
    // headers stacked above the table occupy that space, so push it down past
    // them.
    TableHead: forwardRef(function StickyTableHead({style, context, ...props}, ref) {
      return <BaseTableHead ref={ref} {...props} style={{...style, top: context.stickyOffset}} />;
    }),
    TableBody: forwardRef(function TableBody({context: _context, ...props}, ref) {
      return <BaseTableBody ref={ref} {...props} />;
    }),
    TableRow: ({item, context, ...props}) => (
      <BaseTableRow {...(item.type === "item" ? context.getItemRowProps(item.item) : context.rowProps)} {...props} />
    ),
  };
}

export const VirtualizedTableImpl = typedMemo(
  <C extends TableConfig>({
    config,
    renderRowContent,
    renderHeaderContent,
    shouldRenderGroupHeaderRow,
    renderGroupHeaderContent,
    itemsOrGroups,
    rootProps,
    rowProps,
    getItemRowProps,
    minWidth,
    scrollParent,
  }: TableImplProps<C>) => {
    const [isScrolling, setIsScrolling] = useState(false);
    const [components] = useState(() => createComponents<C>());
    const stickyOffset = useStickyOffset();

    const scrolling = useMemo<ScrollingContext>(() => ({isScrolling}), [isScrolling]);
    const context = useMemo<VirtuosoContext<C>>(
      () => ({scrolling, config, rootProps, rowProps, getItemRowProps, minWidth, stickyOffset}),
      [scrolling, config, rootProps, rowProps, getItemRowProps, minWidth, stickyOffset],
    );

    const generateRows = useCallback(
      (itemsOrGroups: ItemsOrGroups<C>, depth: number, keyPrefix?: Key): Row<C>[] => {
        if (itemsOrGroups.type === "items") {
          return itemsOrGroups.items.map(item => ({type: "item", item, depth, key: generateItemKey(config, item)}));
        }

        return itemsOrGroups.groups.flatMap(group => {
          const groupKey = generateGroupKey(group, keyPrefix);

          return [
            ...(shouldRenderGroupHeaderRow(group, depth)
              ? [{type: "header", group, depth, key: generateGroupKey(group, keyPrefix)} as const]
              : []),
            ...generateRows(group.itemsOrGroups, depth + 1, groupKey),
          ];
        });
      },
      [config, shouldRenderGroupHeaderRow],
    );

    const rows = useMemo<Row<C>[]>(() => generateRows(itemsOrGroups, 0), [generateRows, itemsOrGroups]);

    const itemContent = useCallback<ItemContent<Row<C>, VirtuosoContext<C>>>(
      (_index, rowItem, context) => {
        if (rowItem.type === "header") {
          return renderGroupHeaderContent(rowItem.group, rowItem.depth);
        } else {
          return renderRowContent(rowItem.item, context.scrolling);
        }
      },
      [renderGroupHeaderContent, renderRowContent],
    );

    const computeItemKey = useCallback<ComputeItemKey<Row<C>, unknown>>((_index, item) => item.key, []);

    const useWindowScroll = useMemo(() => scrollParent == null, [scrollParent]);
    const customScrollParent = useMemo(() => scrollParent ?? undefined, [scrollParent]);

    return (
      <TableVirtuoso<Row<C>, VirtuosoContext<C>>
        context={context}
        isScrolling={setIsScrolling}
        data={rows}
        components={components}
        fixedHeaderContent={renderHeaderContent}
        itemContent={itemContent}
        computeItemKey={computeItemKey}
        increaseViewportBy={500}
        useWindowScroll={useWindowScroll}
        customScrollParent={customScrollParent}
      />
    );
  },
);
